import { Controller } from '@hotwired/stimulus'
import { createRoot } from 'react-dom/client'
import { Honeybadger, HoneybadgerErrorBoundary } from '@honeybadger-io/react'

// https://medium.com/@adit.saxena/ruby-on-rails-turbo-stimulus-react-%EF%B8%8F-5fdfb376a9ff

// Note: Eager loading appears to be necessary - lazy loading runs into a MIME type error
const rawModules = {
  ...import.meta.glob('../../components/*.jsx', {eager: true}),
  ...import.meta.glob('../../components/*.tsx', {eager: true})
}

const modules = Object.fromEntries(
  // Strip extension
  Object.entries(rawModules).map(([k, v]) => [k.slice(0,-4), v])
);

function reviver(key, value) {
  if (
    typeof value === 'string' &&
    // We don't need to be too strict here; if it's not a valid date, it will just be a string
    value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)
  ) {
    const a = new Date(value)
    if (!isNaN(a)) { return a }
  }
  return value
}

// Connects to data-controller="utils--react-loader"
export default class extends Controller {
  static values = { component: String }

  // https://medium.com/@amatewasu/how-to-render-a-dom-element-into-a-react-element-a7ce2aa51976
  async connect() {

    if(this.element.getAttribute('data-react-loaded')) return;

    const Component = await this.importComponent(this.componentValue)
    const DomChildren = await this.importComponent("DomChildren")

    const initialProps = this.props()
    const root = createRoot(
      this.element.appendChild(document.createElement('react-root'))
    )

    const honeybadger = Honeybadger.configure(window.honeybadgerConfig)
    honeybadger.setContext({
      component: this.componentValue,
      initialProps: initialProps
    })

    // Initial render
    root.render(
      <HoneybadgerErrorBoundary honeybadger={honeybadger}>
        <Component {...initialProps}>
          { this.rails_children().map( (child, idx) => <DomChildren children_node={child} key={idx}/> ) }
          <DomChildren children_node={this.rails_children_node()}/>
        </Component>
      </HoneybadgerErrorBoundary>
    )

    this.element.setAttribute('data-react-loaded', true)

    // Catch any changes to the props and re-render
    const observer = new MutationObserver(() => {
      const updatedProps = this.props()

      honeybadger.setContext({
        component: this.componentValue,
        initialProps: initialProps,
        updatedProps: updatedProps
      })

      root.render(
        <HoneybadgerErrorBoundary honeybadger={honeybadger}>
          <Component {...updatedProps}>
            { this.rails_children().map( (child, idx) => <DomChildren children_node={child} key={idx}/> ) }
            <DomChildren children_node={this.rails_children_node()}/>
          </Component>
        </HoneybadgerErrorBoundary>
      )
    })

    observer.observe(this.propsRoot(), { childList: true, subtree: true })
  }

  propsRoot() {
    return this.element.querySelectorAll('[name=react-props]')[0]
  }

  rails_children() {
    return Array.from(this.rails_children_node()?.children || [])
  }

  rails_children_node() {
    return this.element.getElementsByTagName('react_rails_children')[0]
  }

  props() {
    return Array.from(
      this.propsRoot().getElementsByTagName('script'),
      (e) => JSON.parse(e.innerText, reviver)
    ).reduce((all, some) => { return {...all, ...some} }, {})
  }

  importComponent(componentValue) {
    const key = `../../components/${componentValue}`
    const mod = modules[key]
    return mod.default
  }
}